// GTM-ready tracking. Pushes events to dataLayer; safe to call even when GTM is not loaded.

const isBrowser = typeof window !== "undefined";

export function trackEvent(event, payload = {}) {
  if (!isBrowser) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...payload });
}

export function getTrackingMeta() {
  if (!isBrowser) return {};
  const params = new URLSearchParams(window.location.search);
  const pick = (k) => params.get(k) || undefined;
  return {
    utm_source: pick("utm_source"),
    utm_medium: pick("utm_medium"),
    utm_campaign: pick("utm_campaign"),
    utm_term: pick("utm_term"),
    utm_content: pick("utm_content"),
    landing_page_url: window.location.href,
    referrer: document.referrer || undefined,
    gclid: pick("gclid"),
    gbraid: pick("gbraid"),
    wbraid: pick("wbraid"),
    fbclid: pick("fbclid"),
    li_fat_id: pick("li_fat_id"),
  };
}

export function scrollToForm() {
  if (!isBrowser) return;
  window.__rekisaHeadingToForm = true;
  const el = document.getElementById("quote-form") || document.getElementById("request-quote") || document.getElementById("request-plan");
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => {
      const first = el.querySelector("input, select, textarea, [tabindex]");
      if (first) first.focus({ preventScroll: true });
    }, 600);
  }
}

// ============================================================================
// GTM event helpers — spec'd by Thato (May 2026)
// Use these so service_name + human-readable plan / addon labels are guaranteed
// consistent across every page and every callsite.
// ============================================================================

// Service taxonomy — pass one of these as service_name on every event.
export const SERVICE = {
  paid_ads: "paid_ads",
  whatsapp: "whatsapp_automation",
  website: "website_app_creation",
  ecommerce: "ecommerce_store_setup",
};

// Plan ID (used internally in forms / pricing components) → human-readable label
// matching the pricing cards on the live site. Edit only if a pricing card changes.
export const PLAN_LABEL = {
  // Paid Ads
  dwy: "DWY Paid Ads Enablement",
  dfy: "DFY Paid Ads Management",
  growth: "Growth Paid Ads System",
  // WhatsApp
  diy: "DIY with Chatfuel",            // WA context
  wa_dwy: "DWY WhatsApp Automation Enablement",
  wa_dfy: "DFY WhatsApp Automation Setup",
  // Website / App
  web_diy: "DIY with Emergent",
  web_dwy: "DWY Website / App Enablement",
  dfy_website: "DFY Website Launch",
  dfy_app: "DFY App / MVP Build",
};

// Addon ID → human-readable label (matches the pricing cards exactly).
export const ADDON_LABEL = {
  // Paid Ads add-ons (PricingSection.jsx)
  landing_page: "Landing Page Setup",
  followup: "Automated Follow-Up Setup",
  whatsapp: "WhatsApp Automation Management",
  extra_platform: "Additional Ad Platform Management",
  // WhatsApp add-ons (WhatsappPricing.jsx)
  paid_ads: "Paid Ads Setup",
  lead_tracking: "Lead Tracking Integration",
  broadcast: "Broadcast Campaign Setup",
  ai_assistant: "AI Assistant Setup",
};

// Budget value (form select value) → display label. Already human-readable on
// most forms — passthrough for "Not sure yet" etc.
function _budgetLabel(v) {
  return v || "";
}

// Form go-live value → display label.
function _goLiveLabel(v) {
  return v || "";
}

/** Resolves a plan id to its display label, falling back to the id itself. */
export function planLabel(planId, serviceName) {
  if (!planId) return "";
  // Disambiguate "diy" (which both WA and Website share) and "dwy"
  if (serviceName === SERVICE.whatsapp) {
    if (planId === "dwy") return PLAN_LABEL.wa_dwy;
    if (planId === "dfy") return PLAN_LABEL.wa_dfy;
  }
  if (serviceName === SERVICE.website) {
    if (planId === "diy") return PLAN_LABEL.web_diy;
    if (planId === "dwy") return PLAN_LABEL.web_dwy;
  }
  return PLAN_LABEL[planId] || planId;
}

/** Resolves an addon id to its display label, falling back to the id. */
export function addonLabel(addonId) {
  return ADDON_LABEL[addonId] || addonId;
}

// ---------- Event helpers ----------

export function trackFormStart({ service, formName }) {
  if (isBrowser) window.__rekisaFormStarted = true;
  trackEvent("form_start", {
    service_name: service,
    form_name: formName,
  });
}

export function trackQuoteSubmit({ service, planId, addonIds = [], budget, goLive, leadType = "quote_request" }) {
  trackEvent("quote_request_submit", {
    service_name: service,
    selected_plan: planLabel(planId, service),
    selected_addons: (addonIds || []).map(addonLabel),
    budget_range: _budgetLabel(budget),
    go_live_timeline: _goLiveLabel(goLive),
    lead_type: leadType,
  });
}

export function trackPlanSelected({ service, planId }) {
  trackEvent("pricing_plan_selected", {
    service_name: service,
    selected_plan: planLabel(planId, service),
  });
}

export function trackAddonSelected({ service, addonId }) {
  trackEvent("addon_selected", {
    service_name: service,
    addon_name: addonLabel(addonId),
  });
}

export function trackDiyReferralClick({ service, partnerName, referralUrl }) {
  trackEvent("diy_referral_click", {
    service_name: service,
    partner_name: partnerName,   // "Chatfuel" | "Emergent" | "Ecwid"
    referral_url: referralUrl,
  });
}

export function trackMeetingLinkClick({ service, linkUrl }) {
  trackEvent("meeting_link_click", {
    service_name: service,
    link_url: linkUrl,
  });
}

export function trackPhoneClick({ service, phoneNumber, location }) {
  trackEvent("phone_click", {
    service_name: service,
    phone_number: phoneNumber,
    click_location: location,
  });
}

export function trackEmailClick({ service, emailAddress, location }) {
  trackEvent("email_click", {
    service_name: service,
    email_address: emailAddress,
    click_location: location,
  });
}

export function trackWhatsappClick({ service, whatsappNumber, location }) {
  trackEvent("whatsapp_click", {
    service_name: service,
    whatsapp_number: whatsappNumber,
    click_location: location,
  });
}

// Google Ads conversion: WhatsApp Automation - Quote Submitted.
// Fires once per backend-confirmed lead id; uses the AW Google tag already loaded by GTM.
export const WA_QUOTE_CONVERSION = "AW-18127541664/uXe9CMWWg58dEKCr8cND";

export function trackWhatsappQuoteSubmitted({ leadId, planId, budget, goLive }) {
  if (!isBrowser || !leadId) return;
  const key = `wa_conv_${leadId}`;
  try {
    if (window.sessionStorage.getItem(key)) return;
    window.sessionStorage.setItem(key, "1");
  } catch (_) { /* storage blocked: rely on caller's once-guard */ }
  trackEvent("whatsapp_quote_submitted", {
    service_name: SERVICE.whatsapp,
    lead_id: leadId,
    selected_plan: planLabel(planId, SERVICE.whatsapp),
    budget_range: budget,
    go_live_timeline: goLive,
    conversion_send_to: WA_QUOTE_CONVERSION,
  });
  window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
  window.gtag("event", "conversion", { send_to: WA_QUOTE_CONVERSION, transaction_id: leadId });
}

// Google Ads conversion: Sales System - Plan Submitted (+ GA4 event via the GTM-loaded Google tag).
export const SALES_PLAN_CONVERSION = "AW-18127541664/2DfuCNaWhqcdEKCr8cND";
const GA4_ID = "G-TK5695S0JD";

export function trackSalesPlanSubmitted({ leadId, projectType, budget, goLive }) {
  if (!isBrowser || !leadId) return;
  const key = `sales_conv_${leadId}`;
  try {
    if (window.sessionStorage.getItem(key)) return;
    window.sessionStorage.setItem(key, "1");
  } catch (_) { /* storage blocked: caller's in-flight guard still applies */ }
  const params = {
    service_name: SERVICE.website,
    lead_id: leadId,
    project_type: projectType,
    budget_range: budget,
    go_live_timeline: goLive,
  };
  trackEvent("sales_system_plan_submitted", { ...params, conversion_send_to: SALES_PLAN_CONVERSION });
  window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
  window.gtag("event", "conversion", { send_to: SALES_PLAN_CONVERSION, transaction_id: leadId });
  window.gtag("event", "sales_system_plan_submitted", { ...params, transaction_id: leadId, send_to: GA4_ID });
}

// Google Ads conversion: eCommerce - Quote Submitted (+ GA4 event via the GTM-loaded Google tag).
export const ECOM_QUOTE_CONVERSION = "AW-18127541664/npGMCNPq5awdEKCr8cND";

export function trackEcommerceQuoteSubmitted({ leadId, serviceInterest, marketplaces, timeframe }) {
  if (!isBrowser || !leadId) return;
  const key = `ecom_conv_${leadId}`;
  try {
    if (window.sessionStorage.getItem(key)) return;
    window.sessionStorage.setItem(key, "1");
  } catch (_) { /* storage blocked: caller's in-flight guard still applies */ }
  const params = {
    service_name: SERVICE.ecommerce,
    lead_id: leadId,
    service_interest: serviceInterest,
    marketplaces: (marketplaces || []).join(", "),
    go_live_timeline: timeframe,
  };
  trackEvent("ecommerce_quote_submitted", { ...params, conversion_send_to: ECOM_QUOTE_CONVERSION });
  window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
  window.gtag("event", "conversion", { send_to: ECOM_QUOTE_CONVERSION, transaction_id: leadId });
  window.gtag("event", "ecommerce_quote_submitted", { ...params, transaction_id: leadId, send_to: GA4_ID });
}
